import React, { FC, useCallback, useEffect, useState } from 'react';
import { AverageLeadFollowUpTime, AveragePurchaseTime, ChassisTypeQuantity, LeadsByLeadType, LeadStatisticsData, RegionQuantity, StateQuantity } from '../../@types/Lead';
import axios from '../../api/axios';
import { BarChart, Gauge, PieChart, PieValueType, gaugeClasses } from '@mui/x-charts';
import Card from '../common/Card/Card';
import { Grid, Skeleton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import moment from 'moment';

interface LeadStatisticsProps {
   dealerId?: string
}

const LeadStatistics: FC<LeadStatisticsProps> = ({ dealerId }) => {
   const [leadStats, setLeadStats] = useState<LeadStatisticsData>();
   const [loading, setLoading] = useState<boolean>(false);

   const loadLeadStats = useCallback(async () => {
      try {
         setLoading(true);
         const queryParams: any = {};
         if (dealerId) queryParams.dealerId = dealerId;
         const response = await axios.get('/statistics/leads', { params: queryParams });
         const data: LeadStatisticsData = response.data as LeadStatisticsData;
         setLeadStats(data);
      } catch (error) {
         console.log(error);
      } finally {
         setLoading(false);
      }
   }, [])

   useEffect(() => {
      loadLeadStats();
   }, [loadLeadStats]);

   return (
      <Grid container spacing={2}>
         <Grid size={{ desktop: 2, laptopL: 4, tablet: 7, tabletS: 12, mobileS: 12 }}>
            <Stack direction="column" spacing={2}>
               <TotalLeadsChart
                  loading={loading}
                  totalLeads={leadStats?.totalLeads ?? 0} />
               <TotalLeadsNotViewedChart
                  loading={loading}
                  totalLeadsNotViewed={leadStats?.totalLeadsNotViewed ?? 0} />
               <TotalLeadsNotFollowedUpChart
                  loading={loading}
                  totalLeadsNotFollowedUp={leadStats?.totalLeadsNotFollowedUp ?? 0} />
               <LeadConversionsChart
                  loading={loading}
                  totalLeadConversions={leadStats?.totalLeadConversions ?? 0} />
            </Stack>
         </Grid>
         <Grid size={{ desktop: 5, laptopL: 4, tablet: 6, tabletS: 12, mobileS: 12 }}>
            <LeadAverageConversionTimeChart
               loading={loading}
               leadAveragePurchaseTime={leadStats?.leadAveragePurchaseTime} />
         </Grid>
         <Grid size={{ desktop: 5, laptopL: 4, tablet: 6, tabletS: 12, mobileS: 12 }}>
            <LeadAverageFollowUpTimeChart
               loading={loading}
               leadAverageFollowUpTime={leadStats?.leadAverageFollowUpTime} />
         </Grid>
         <Grid size={{ desktop: 4, laptopL: 4, tabletS: 12, mobileS: 12 }}>
            <LeadsByChassisTypeChart
               loading={loading}
               leadsByChassisType={leadStats?.leadsByChassisType}
            />
         </Grid>
         <Grid size={{ desktop: 4, laptopL: 4, tabletS: 12, mobileS: 12 }}>
            <LeadsByRegionChart
               loading={loading}
               totalLeadsByRegion={leadStats?.totalLeadsByRegion ?? []} />
         </Grid>
         <Grid size={{ desktop: 4, laptopL: 4, tabletS: 12, mobileS: 12 }}>
            <LeadsByLeadTypeChart
               loading={loading}
               totalLeadsByLeadType={leadStats?.leadsByLeadType ?? []} />
         </Grid>
         <Grid size={{ desktop: 12, laptopL: 12, tabletS: 12, mobileS: 12 }}>
            <LeadsByUSStateChart
               loading={loading}
               totalLeadsByState={leadStats?.totalLeadsByState ?? []} />
         </Grid>
      </Grid>
   );
}


interface TotalLeadsNotFollowedUpChartProps {
   loading: boolean,
   totalLeadsNotFollowedUp: number
}

const TotalLeadsNotFollowedUpChart: FC<TotalLeadsNotFollowedUpChartProps> = ({ loading, totalLeadsNotFollowedUp }) => {
   return (
      <Card>
         <ChartTitle title={'leads not followed up'} />
         {loading
            ? (<Skeleton animation="wave" width={'100%'} height={28} />)
            : (<Typography sx={{ color: totalLeadsNotFollowedUp > 0 ? 'red' : 'green' }}>{totalLeadsNotFollowedUp}</Typography>)
         }

      </Card>
   )
}

interface TotalLeadsNotViewedChartProps {
   loading: boolean,
   totalLeadsNotViewed: number
}

const TotalLeadsNotViewedChart: FC<TotalLeadsNotViewedChartProps> = ({ loading, totalLeadsNotViewed }) => {
   return (
      <Card>
         <ChartTitle title={'leads not viewed'} />
         {loading
            ? (<Skeleton animation="wave" width={'100%'} height={28} />)
            : (<Typography sx={{ color: totalLeadsNotViewed > 0 ? 'red' : 'green' }}>{totalLeadsNotViewed}</Typography>)
         }
      </Card>
   )
}

interface TotalLeadsChartProps {
   loading: boolean,
   totalLeads: number,
}

const TotalLeadsChart: FC<TotalLeadsChartProps> = ({ loading, totalLeads }) => {
   return (
      <Card>
         <ChartTitle title={'total leads'} />
         {loading
            ? (<Skeleton animation="wave" width={'100%'} height={28} />)
            : (<Typography>{totalLeads}</Typography>)
         }
      </Card>
   )
}

interface LeadConversionsChartProps {
   loading: boolean,
   totalLeadConversions: number,
}

const LeadConversionsChart: FC<LeadConversionsChartProps> = ({ loading, totalLeadConversions }) => {
   return (
      <Card>
         <ChartTitle title={'lead conversions'} />
         {loading
            ? (<Skeleton animation="wave" width={'100%'} height={28} />)
            : (<Typography>{totalLeadConversions}</Typography>)
         }
      </Card>
   )
}

interface LeadsByChassisTypeChartProps {
   loading: boolean,
   leadsByChassisType: ChassisTypeQuantity[] | undefined
}

const LeadsByChassisTypeChart: FC<LeadsByChassisTypeChartProps> = ({ loading, leadsByChassisType }) => {
   const data: PieValueType[] = [];

   for (const chassisType of leadsByChassisType ?? []) {
      data.push({
         id: chassisType.chassisType,
         value: chassisType.quantity,
         label: chassisType.chassisType
      })
   }

   return (
      <Card>
         <Stack direction="column" spacing={2}>
            <ChartTitle title={'leads by chassis type'} />
            <PieChart
               series={[{ data: data }]}
               width={300}
               height={300}
            />
         </Stack>
      </Card>
   )
}

interface LeadAverageConversionTimeChartProps {
   loading: boolean,
   leadAveragePurchaseTime: AveragePurchaseTime | undefined,
}

const LeadAverageConversionTimeChart: FC<LeadAverageConversionTimeChartProps> = ({ loading, leadAveragePurchaseTime }) => {
   const theme = useTheme();
   const isDesktop = useMediaQuery(theme.breakpoints.up('desktop'));

   const goalDays = 14;
   const goalMoment = moment.duration(goalDays, 'days');
   const avgMoment = moment.duration({
      days: leadAveragePurchaseTime?.averagePurchaseTime?.days,
      hours: leadAveragePurchaseTime?.averagePurchaseTime?.hours,
      minutes: leadAveragePurchaseTime?.averagePurchaseTime?.minutes,
      seconds: leadAveragePurchaseTime?.averagePurchaseTime?.seconds
   });

   const gaugeValue = loading ? 0 : (goalMoment.asMilliseconds() / avgMoment.asMilliseconds()) * 100;

   const getFillColor = (): string => {
      if (gaugeValue > 75) return 'green';
      if (gaugeValue < 75 && gaugeValue > 50) return 'rgb(255, 180, 34)';
      if (gaugeValue < 50) return 'red';
      return 'red';
   }

   return (
      <Card>
         <Stack direction="column" spacing={2}>
            <ChartTitle title={'average lead conversion time'} />
            <Stack
               sx={(theme) => ({
                  width: '100%',
                  flexDirection: 'row',
                  [theme.breakpoints.down('desktop')]: {
                     flexDirection: 'column'
                  },
               })}
               spacing={2}
               justifyContent="space-evenly"
               alignItems="center">
               <Gauge
                  sx={(theme) => ({
                     [`& .${gaugeClasses.valueText}`]: {
                        fontVariant: 'secondaryFont',
                        fontSize: '18px',
                        fill: getFillColor()
                     },
                     // You can also target other elements like the arc
                     [`& .${gaugeClasses.valueArc}`]: {
                        fill: getFillColor()
                     },
                  })}
                  width={isDesktop ? 285 : 225}
                  height={isDesktop ? 285 : 225}
                  startAngle={-130}
                  endAngle={130}
                  value={gaugeValue > 100 ? 100 : gaugeValue}
                  text={`${loading ? '' : `Goal < ${goalDays} Days
                     ${leadAveragePurchaseTime?.averagePurchaseTime?.days === null && leadAveragePurchaseTime?.averagePurchaseTime?.hours === null && leadAveragePurchaseTime?.averagePurchaseTime?.minutes === null ? 'N/A' : ''}
                     ${leadAveragePurchaseTime?.averagePurchaseTime?.days ? `${leadAveragePurchaseTime.averagePurchaseTime?.days}D:` : ''}${leadAveragePurchaseTime?.averagePurchaseTime?.hours ? `${leadAveragePurchaseTime.averagePurchaseTime?.hours}H:` : ''}${leadAveragePurchaseTime?.averagePurchaseTime?.minutes ? `${leadAveragePurchaseTime.averagePurchaseTime?.minutes}M` : ''}
                  `}`}>
               </Gauge>
               <Stack direction="column" spacing={4}>
                  <Stack direction="column" spacing={1}>
                     <Typography>Longest Conversion Time</Typography>
                     {loading
                        ? (
                           <Skeleton animation="wave" width={'100%'} height={28} />
                        ) : (
                           <Typography textAlign="center">
                              {leadAveragePurchaseTime?.longestPurchaseTime?.days === 0 && leadAveragePurchaseTime?.longestPurchaseTime?.hours === 0 && leadAveragePurchaseTime?.longestPurchaseTime?.minutes === 0 ? 'N/A' : ''}
                              {leadAveragePurchaseTime?.longestPurchaseTime?.days ? `${leadAveragePurchaseTime?.longestPurchaseTime?.days}D:` : ''}
                              {leadAveragePurchaseTime?.longestPurchaseTime?.hours ? `${leadAveragePurchaseTime?.longestPurchaseTime?.hours}H:` : ''}
                              {leadAveragePurchaseTime?.longestPurchaseTime?.minutes ? `${leadAveragePurchaseTime?.longestPurchaseTime?.minutes}M` : ''}
                           </Typography>
                        )}
                  </Stack>
                  <Stack direction="column" spacing={1}>
                     <Typography>Shortest Conversion Time</Typography>
                     {loading
                        ? (
                           <Skeleton animation="wave" width={'100%'} height={28} />
                        ) : (
                           <Typography textAlign="center">
                              {leadAveragePurchaseTime?.shortestPurchaseTime?.days === 0 && leadAveragePurchaseTime?.shortestPurchaseTime?.hours === 0 && leadAveragePurchaseTime?.shortestPurchaseTime?.minutes === 0 ? 'N/A' : ''}
                              {leadAveragePurchaseTime?.shortestPurchaseTime?.days ? `${leadAveragePurchaseTime?.shortestPurchaseTime?.days}D:` : ''}
                              {leadAveragePurchaseTime?.shortestPurchaseTime?.hours ? `${leadAveragePurchaseTime?.shortestPurchaseTime?.hours}H:` : ''}
                              {leadAveragePurchaseTime?.shortestPurchaseTime?.minutes ? `${leadAveragePurchaseTime?.shortestPurchaseTime?.minutes}M` : ''}
                           </Typography>
                        )}
                  </Stack>
               </Stack>
            </Stack>
         </Stack>
      </Card>
   )
}

interface LeadAverageFollowUpTimeChartProps {
   loading: boolean,
   leadAverageFollowUpTime: AverageLeadFollowUpTime | undefined,
}

const LeadAverageFollowUpTimeChart: FC<LeadAverageFollowUpTimeChartProps> = ({ loading, leadAverageFollowUpTime }) => {
   const theme = useTheme();
   const isDesktop = useMediaQuery(theme.breakpoints.up('desktop'));

   const goalDays = 1;
   const goalMoment = moment.duration(goalDays, 'days');
   const avgMoment = moment.duration({
      days: leadAverageFollowUpTime?.averageResponseTime?.days,
      hours: leadAverageFollowUpTime?.averageResponseTime?.hours,
      minutes: leadAverageFollowUpTime?.averageResponseTime?.minutes,
      seconds: leadAverageFollowUpTime?.averageResponseTime?.seconds
   });

   const gaugeValue = loading ? 0 : (goalMoment.asMilliseconds() / avgMoment.asMilliseconds()) * 100;

   const getFillColor = (): string => {
      if (gaugeValue > 75) return 'green';
      if (gaugeValue < 75 && gaugeValue > 50) return 'rgb(255, 180, 34)';
      if (gaugeValue < 50) return 'red';
      return 'red';
   }

   return (
      <Card>
         <Stack direction="column" spacing={2}>
            <ChartTitle title={'average lead follow up time'} />
            <Stack sx={(theme) => ({
               width: '100%',
               flexDirection: 'row',
               [theme.breakpoints.down('desktop')]: {
                  flexDirection: 'column'
               },
            })} spacing={2} justifyContent="space-evenly" alignItems="center">
               <Gauge
                  sx={(theme) => ({
                     [`& .${gaugeClasses.valueText}`]: {
                        fontVariant: 'secondaryFont',
                        fontSize: '18px',
                        fill: getFillColor()
                     },
                     // You can also target other elements like the arc
                     [`& .${gaugeClasses.valueArc}`]: {
                        fill: getFillColor()
                     },
                  })}
                  width={isDesktop ? 285 : 225}
                  height={isDesktop ? 285 : 225}
                  startAngle={-130}
                  endAngle={130}
                  value={gaugeValue > 100 ? 100 : gaugeValue}
                  text={`${loading ? '' : `Goal < ${goalDays} Days
                     ${leadAverageFollowUpTime?.averageResponseTime?.days === null && leadAverageFollowUpTime?.averageResponseTime?.hours === null && leadAverageFollowUpTime?.averageResponseTime?.minutes === null ? 'N/A' : ''}
                     ${leadAverageFollowUpTime?.averageResponseTime?.days ? `${leadAverageFollowUpTime.averageResponseTime?.days}D:` : ''}${leadAverageFollowUpTime?.averageResponseTime?.hours ? `${leadAverageFollowUpTime.averageResponseTime?.hours}H:` : ''}${leadAverageFollowUpTime?.averageResponseTime?.minutes ? `${leadAverageFollowUpTime.averageResponseTime?.minutes}M` : ''}
                  `}`}>
               </Gauge>
               <Stack direction="column" spacing={4}>
                  <Stack direction="column" spacing={1}>
                     <Typography>Slowest Follow Up Time</Typography>
                     {loading
                        ? (
                           <Skeleton animation="wave" width={'100%'} height={28} />
                        ) : (
                           <Typography textAlign="center">
                              {leadAverageFollowUpTime?.longestResponseTime?.days === 0 && leadAverageFollowUpTime?.longestResponseTime?.hours === 0 && leadAverageFollowUpTime?.longestResponseTime?.minutes === 0 ? 'N/A' : ''}
                              {leadAverageFollowUpTime?.longestResponseTime?.days ? `${leadAverageFollowUpTime?.longestResponseTime?.days}D:` : ''}
                              {leadAverageFollowUpTime?.longestResponseTime?.hours ? `${leadAverageFollowUpTime?.longestResponseTime?.hours}H:` : ''}
                              {leadAverageFollowUpTime?.longestResponseTime?.minutes ? `${leadAverageFollowUpTime?.longestResponseTime?.minutes}M` : ''}
                           </Typography>
                        )}
                  </Stack>
                  <Stack direction="column" spacing={1}>
                     <Typography>Fastest Follow Up Time</Typography>
                     {loading
                        ? (
                           <Skeleton animation="wave" width={'100%'} height={28} />
                        ) : (
                           <Typography textAlign="center">
                              {leadAverageFollowUpTime?.shortestResponseTime?.days === 0 && leadAverageFollowUpTime?.shortestResponseTime?.hours === 0 && leadAverageFollowUpTime?.shortestResponseTime?.minutes === 0 ? 'N/A' : ''}
                              {leadAverageFollowUpTime?.shortestResponseTime?.days ? `${leadAverageFollowUpTime?.shortestResponseTime?.days}D:` : ''}
                              {leadAverageFollowUpTime?.shortestResponseTime?.hours ? `${leadAverageFollowUpTime?.shortestResponseTime?.hours}H:` : ''}
                              {leadAverageFollowUpTime?.shortestResponseTime?.minutes ? `${leadAverageFollowUpTime?.shortestResponseTime?.minutes}M` : ''}
                           </Typography>
                        )}
                  </Stack>
               </Stack>
            </Stack>
         </Stack>
      </Card>
   )
}

interface LeadsByRegionChartProps {
   loading: boolean,
   totalLeadsByRegion: RegionQuantity[];
}

const LeadsByRegionChart: FC<LeadsByRegionChartProps> = ({ loading, totalLeadsByRegion }) => {

   const data: PieValueType[] = [];

   for (const region of totalLeadsByRegion) {
      data.push({
         id: region.region,
         value: region.quantity,
         label: region.region
      })
   }

   return (
      <Card>
         <Stack direction="column" spacing={2}>
            <ChartTitle title={'total leads by region'} />
            <PieChart
               series={[{ data: data }]}
               width={300}
               height={300}
            />
         </Stack>
      </Card>
   )
}

interface LeadsByLeadTypeChartProps {
   loading: boolean,
   totalLeadsByLeadType: LeadsByLeadType[];
}

const LeadsByLeadTypeChart: FC<LeadsByLeadTypeChartProps> = ({ loading, totalLeadsByLeadType }) => {

   const data: PieValueType[] = [];

   for (const region of totalLeadsByLeadType) {
      data.push({
         id: region.leadType,
         value: region.quantity,
         label: region.leadType
      })
   }

   return (
      <Card>
         <Stack direction="column" spacing={2}>
            <ChartTitle title={'total leads by lead type'} />
            <PieChart
               series={[{ data: data }]}
               width={300}
               height={300}
            />
         </Stack>
      </Card>
   )
}

interface LeadsByUSStateChartProps {
   loading: boolean,
   totalLeadsByState: StateQuantity[]
}

const LeadsByUSStateChart: FC<LeadsByUSStateChartProps> = ({ loading, totalLeadsByState }) => {

   const data: any[] = [];
   const dataKeys: any[] = [];

   const valueFormatter = (value: number | null) => {
      return value ?? 0;
   }

   for (const d of totalLeadsByState) {

      const { state, stateCode } = d;

      const rData: any = { state, stateCode };

      for (const leadType of d.leads) {

         rData[`${leadType.leadType}`] = leadType.quantity;

         const keyExists = dataKeys.some((dKey) =>  dKey.dataKey === leadType.leadType);

         if (!keyExists) {

            dataKeys.push({ dataKey: leadType.leadType, label: leadType.leadType, valueFormatter });

         }

      }

      data.push(rData);

   }

   

   return (
      <Card>
         <Stack direction="column" spacing={2}>
            <ChartTitle title={'leads by US state'} />
            <BarChart
               dataset={data}
               series={dataKeys}
               height={300}
               yAxis={[{ label: 'Total Leads', tickMinStep: 1 }]}
               xAxis={[{ 
                  dataKey: 'stateCode', 
                  label: 'US States',
                  valueFormatter: (stateCode: any, context: any) => 
                     context.location === 'tick'
                  ? stateCode
                  : `${data.find((d) => d.stateCode === stateCode)?.state}`
               }]}
            />
         </Stack>
      </Card>
   )
}

interface ChartTitleProps {
   title: string,
}

const ChartTitle: FC<ChartTitleProps> = ({ title }) => {
   return (
      <Typography variant="primaryFont" fontSize={12} textTransform={"uppercase"}>{title}</Typography>
   )
}

export default LeadStatistics;
